import React from 'react';
import { ArrowLeft } from 'phosphor-react';
import { Button } from './Button';
import { cn } from '../utils/cn';

export interface PageNavBarProps {
  /**
   * Si es true, la barra será sticky (por defecto true)
   */
  sticky?: boolean;
  
  /**
   * Clases CSS adicionales
   */
  className?: string;
  
  /**
   * Slot izquierdo - Si no se proporciona y hay onBack, se renderiza botón back por defecto
   */
  left?: React.ReactNode;
  
  /**
   * Slot centro - Si no se proporciona y hay title, se renderiza título por defecto
   */
  center?: React.ReactNode;
  
  /**
   * Slot derecho - Acciones opcionales
   */
  right?: React.ReactNode;
  
  /**
   * Callback para el botón de volver (solo si no se proporciona left)
   */
  onBack?: () => void;
  
  /**
   * Título de la página (solo si no se proporciona center)
   */
  title?: string;
  
  /**
   * Acciones a la derecha (alias de right para conveniencia)
   */
  actions?: React.ReactNode;
  
  /**
   * Contenido completamente custom - Si se proporciona, ignora todas las demás props
   * Útil para casos complejos como OrderDetail con lógica de scroll
   */
  children?: React.ReactNode;
}

export const PageNavBar: React.FC<PageNavBarProps> = ({
  sticky = true,
  className = '',
  left,
  center,
  right,
  onBack,
  title,
  actions,
  children
}) => {
  // Si hay children, renderizar completamente custom
  if (children) {
    return (
      <div className={cn(
        'bg-muted flex items-center justify-between px-3 py-2 shrink-0 w-full',
        sticky && 'sticky top-0 z-10',
        className
      )}>
        <div className="flex-1 flex items-center justify-center max-w-[1280px] mx-auto w-full">
          {children}
        </div>
      </div>
    );
  }

  // Renderizar con slots
  const leftContent = left || (onBack ? (
    <Button
      variant="ghost"
      size="icon"
      onClick={onBack}
      className="hover:bg-accent/5"
      aria-label="Volver"
    >
      <ArrowLeft size={20} weight="bold" />
    </Button>
  ) : null);

  const centerContent = center || (title ? (
    <h1 className="font-sans font-semibold text-base text-foreground tracking-[-0.16px]">
      {title}
    </h1>
  ) : null);

  const rightContent = right || actions;

  return (
    <div className={cn(
      'bg-muted flex items-center justify-between px-3 py-2 shrink-0 w-full',
      sticky && 'sticky top-0 z-10',
      className
    )}>
      <div className="flex-1 flex items-center justify-center max-w-[1280px] mx-auto w-full">
        <div className="flex-1 flex items-center gap-2">
          {leftContent}
          {centerContent}
        </div>
        {rightContent && (
          <div className="ml-auto">
            {rightContent}
          </div>
        )}
      </div>
    </div>
  );
};

export default PageNavBar;

